import React, { useEffect } from 'react'
import { useLocalStorage } from 'app/hooks'

export interface AdminSidebarTab {
    id: string
    label: string
    icon: string
    badge?: string // Optional badge (e.g., ⭐) to display in top-right corner
}

interface AdminSidebarProps {
    tabs: AdminSidebarTab[]
    activeTab: string
    onTabChange: (tabId: string) => void
    headerTitle?: string
    storageKey?: string // Optional key for localStorage, defaults to 'admin-sidebar-tab'
    isAdmin?: boolean
    screenShareActive?: boolean
    onScreenShareToggle?: () => void
    screenShareZoom?: number
    onScreenShareZoomIn?: () => void
    onScreenShareZoomOut?: () => void
    onScreenShareZoomSet?: (zoom: number) => void
}

export function AdminSidebar(props: AdminSidebarProps) {
    const {
        tabs,
        activeTab,
        onTabChange,
        headerTitle,
        storageKey = 'admin-sidebar-tab',
        isAdmin,
        screenShareActive,
        onScreenShareToggle,
        screenShareZoom,
        onScreenShareZoomIn,
        onScreenShareZoomOut,
        onScreenShareZoomSet,
    } = props
    
    // Use localStorage to remember the last selected tab
    const [lastSelectedTab, setLastSelectedTab] = useLocalStorage(storageKey, activeTab)
    
    // When component mounts, if there's a stored tab and it exists in current tabs, use it
    useEffect(() => {
        if (lastSelectedTab && tabs.some(tab => tab.id === lastSelectedTab) && lastSelectedTab !== activeTab) {
            onTabChange(lastSelectedTab)
        }
    }, []) // Only run on mount
    
    // Handle tab change and store it
    const handleTabChange = (tabId: string) => {
        setLastSelectedTab(tabId)
        onTabChange(tabId)
    }

    return (
        <div className="analytics-sidebar">
            {headerTitle && (
                <div className="sidebar-header">
                    <h3>{headerTitle}</h3>
                </div>
            )}
            <div className="sidebar-tabs">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        className={`sidebar-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => handleTabChange(tab.id)}
                    >
                        <i className={tab.icon}></i>
                        <span>{tab.label}</span>
                        {tab.badge && <span className="sidebar-tab-badge">{tab.badge}</span>}
                    </button>
                ))}
            </div>
            {isAdmin && onScreenShareToggle && (
                <div className="sidebar-footer">
                    {screenShareActive && onScreenShareZoomIn && onScreenShareZoomOut && (
                        <div className="sidebar-zoom-controls">
                            <div className="sidebar-zoom-label">Data Zoom</div>
                            <div className="sidebar-zoom-row">
                                <button className="sidebar-zoom-step" onClick={onScreenShareZoomOut} title="Zoom out">-</button>
                                <span className="sidebar-zoom-value">{Math.round((screenShareZoom || 1) * 100)}%</span>
                                <button className="sidebar-zoom-step" onClick={onScreenShareZoomIn} title="Zoom in">+</button>
                            </div>
                            <input
                                className="sidebar-zoom-slider"
                                type="range"
                                min={100}
                                max={250}
                                step={10}
                                value={Math.round((screenShareZoom || 1) * 100)}
                                onChange={(e) => onScreenShareZoomSet && onScreenShareZoomSet(Number(e.target.value) / 100)}
                                aria-label="Screen share zoom"
                            />
                        </div>
                    )}
                    <button
                        className={`sidebar-screen-share-btn${screenShareActive ? ' active' : ''}`}
                        onClick={onScreenShareToggle}
                        title={screenShareActive ? 'Exit Screen Share Mode (Esc)' : 'Screen Share Mode'}
                    >
                        <i className={screenShareActive ? 'fas fa-compress-alt' : 'fas fa-expand-alt'} />
                        <span>{screenShareActive ? 'Exit Screen Share' : 'Screen Share'}</span>
                    </button>
                </div>
            )}
        </div>
    )
}